<template>
  <v-text-field
      :error-messages="error"
      :label="translation(translationList.cnp, 'CNP')"
      v-model="cnp"
      :rules="cnpValidationRules"
      name="cnp"
      variant="outlined"
      :tile="true"
      counter="13"
      type="number"
  >
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, toRefs, watch } from 'vue';
import { useTranslationStore, translation } from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: String as () => string | null,
    customerIndex: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue, customerIndex } = toRefs(props);
    const cnp = ref(modelValue.value ? modelValue.value : '');
    const error = ref([]);

    watch(cnp, (newValue) => {
      const isValid = validateCnp(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', '');
      }
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `cnp-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: `cnp`, isValid });
      }
    });

    watch(modelValue, (newValue) => {
     if (newValue && validateCnp(newValue)) {
       cnp.value = newValue;
     }
    });

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const cnpValidationRules = [
      (v: string) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: string) => /^[1-9]\d{12}$/.test(v) || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
      (v: string) => validateCnp(v) || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    function validateCnp(cnp: string, showErrors?: boolean): boolean {
      error.value = [];
      if (showErrors) {
        if (!cnp) {
          error.value = [translation(translationList.value.required, 'Acest câmp este obligatoriu')];
          return false;
        }
      }
      if (!/^[1-9]\d{12}$/.test(cnp)) return false;
      const controlKey = "279146358279";
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(cnp.charAt(i)) * parseInt(controlKey.charAt(i));
      }
      const remainder = sum % 11;
      const controlDigit = remainder < 10 ? remainder : 1;

      const result = controlDigit === parseInt(cnp.charAt(12));

      if (showErrors) {
        if (!result) {
          error.value = [translation(translationList.value.invalid_field, 'Acest câmp este invalid.')];
          return false;
        }
      }
      return result;
    }

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      const isValid = validateCnp(cnp.value);
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `cnp-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: `cnp`, isValid });
      }

// Listen for validate events
      window.addEventListener('validate-customer', handleValidationEvent);
      window.addEventListener('validate-travel', handleValidationEvent);
    });

    const handleValidationEvent = (data: any) => {
      if (data.detail && (data.detail === `cnp-${customerIndex.value}` || data.detail === `cnp`)) {
        const isValid = validateCnp(cnp.value, true);
        if (isValid) {
          emit('validation-customer-status', { field: data.detail, isValid });
        }
      }
    };

    return {
      cnp,
      cnpValidationRules,
      translationList,
      translation,
      error,
    };
  },
});
</script>

<style scoped>
</style>
